import React, { useState, useEffect } from 'react';
import './AddPostModal.css';
import { config } from '../../config';

interface AddPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  storyId: string;
  storyData?: {
    l0_category: string;
    l1_category: string;
    l2_category: string;
    language: string;
    author: string;
  };
  onPostCreated?: () => void;
  editingPost?: {
    id: string;
    title: string;
    description: string;
    language: string;
    script: string;
    urls: string[];
    tags: string[];
    post_type: string;
    duration: number;
    l0_category: string;
    l1_category: string;
    l2_category: string;
    background_audio_urls: string[];
    background_color: string;
    text_color: string;
    type: string;
    interaction_metadata: any[] | null;
    system_type?: string | null;
    system_metadata?: { courses: Array<{ id: string; image_url: string }> };
    author_id?: string;
  };
}

const AddPostModal: React.FC<AddPostModalProps> = ({
  isOpen,
  onClose,
  storyId,
  storyData,
  onPostCreated,
  editingPost,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [postType, setPostType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [masteryChallenges, setMasteryChallenges] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    language: storyData?.language || '',
    script: '',
    urls: [''],
    tags: '',
    duration: '',
    l0_category: storyData?.l0_category || '',
    l1_category: storyData?.l1_category || '',
    l2_category: storyData?.l2_category || '',
    post_type: '',
    background_color: '',
    text_color: '',
    question: '',
    options: ['', '', '', ''],
    correct_options: [] as number[],
    background_audio_urls: [''],
    author_id: storyData?.author || '',
    system_type: '',
    course_id: '',
    image_url: '',
    courses: [{ course_id: '', image_url: '' }],
    system_background_color: '',
    thumbnail: '',
    content_life_in_days: '',
    lrc_url: '',
    show_why: '',
  });

  useEffect(() => {
    if (editingPost) {
      setPostType(editingPost.type);
      setFormData({
        title: editingPost.title || '',
        description: editingPost.description || '',
        language: editingPost.language || storyData?.language || '',
        script: editingPost.script || '',
        urls: editingPost.urls?.length ? editingPost.urls : [''],
        tags: editingPost.tags?.join(', ') || '',
        duration: editingPost.duration?.toString() || '',
        l0_category: editingPost.l0_category || storyData?.l0_category || '',
        l1_category: editingPost.l1_category || storyData?.l1_category || '',
        l2_category: editingPost.l2_category || storyData?.l2_category || '',
        post_type: editingPost.post_type || '',
        background_color: editingPost.background_color || '',
        text_color: editingPost.text_color || '',
        question: editingPost.interaction_metadata?.[0]?.question || '',
        options: editingPost.interaction_metadata?.[0]?.options || [
          '',
          '',
          '',
          '',
        ],
        correct_options:
          editingPost.interaction_metadata?.[0]?.correct_options || [],
        background_audio_urls: editingPost.background_audio_urls?.length
          ? editingPost.background_audio_urls
          : [''],
        author_id: editingPost.author_id || storyData?.author || '',
        system_type: editingPost.system_type || '',
        course_id: editingPost.system_metadata?.courses?.[0]?.id || '',
        image_url: '',
        courses: editingPost.system_metadata?.courses?.map(m => ({
          course_id: m.id,
          image_url: m.image_url,
        })) || [{ course_id: '', image_url: '' }],
        system_background_color: '',
        thumbnail: (editingPost as any).thumbnail || '',
        content_life_in_days:
          (editingPost as any).content_life_in_days?.toString() || '',
        lrc_url: (editingPost as any).lrc_url || '',
        show_why: editingPost.interaction_metadata?.[0]?.show_why || '',
      });
      if (editingPost.type === 'system') {
        fetchMasteryChallenges();
      }
    } else {
      setPostType('');
      setFormData({
        title: '',
        description: '',
        language: storyData?.language || '',
        script: '',
        urls: [''],
        tags: '',
        duration: '',
        l0_category: storyData?.l0_category || '',
        l1_category: storyData?.l1_category || '',
        l2_category: storyData?.l2_category || '',
        post_type: '',
        background_color: '',
        text_color: '',
        question: '',
        options: ['', '', '', ''],
        correct_options: [],
        background_audio_urls: [''],
        author_id: storyData?.author || '',
        system_type: '',
        course_id: '',
        image_url: '',
        courses: [{ course_id: '', image_url: '' }],
        system_background_color: '',
        thumbnail: '',
        content_life_in_days: '',
        lrc_url: '',
        show_why: '',
      });
    }
  }, [editingPost, storyData]);

  const fetchMasteryChallenges = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${config.API_BASE_URL}/mastery-challenges/`,
        {
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );
      const data = await response.json();
      console.log('API response:', data);
      console.log('Challenges array:', data.mastery_challenges || data);
      setMasteryChallenges(data.mastery_challenges || data || []);
    } catch (error) {
      console.error('Error fetching mastery challenges:', error);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const handleCourseChange = (index: number, field: string, value: string) => {
    const newCourses = [...formData.courses];
    newCourses[index] = { ...newCourses[index], [field]: value };
    setFormData(prev => ({ ...prev, courses: newCourses }));
  };

  const addCourse = () => {
    setFormData(prev => ({
      ...prev,
      courses: [...prev.courses, { course_id: '', image_url: '' }],
    }));
  };

  const removeCourse = (index: number) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.filter((_, i) => i !== index),
    }));
  };

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...formData.urls];
    newUrls[index] = value;
    setFormData(prev => ({ ...prev, urls: newUrls }));
  };

  const addUrl = () => {
    setFormData(prev => ({ ...prev, urls: [...prev.urls, ''] }));
  };

  const removeUrl = (index: number) => {
    setFormData(prev => ({
      ...prev,
      urls: prev.urls.filter((_, i) => i !== index),
    }));
  };

  const handleBackgroundAudioUrlChange = (index: number, value: string) => {
    const newUrls = [...formData.background_audio_urls];
    newUrls[index] = value;
    setFormData(prev => ({ ...prev, background_audio_urls: newUrls }));
  };

  const addBackgroundAudioUrl = () => {
    setFormData(prev => ({
      ...prev,
      background_audio_urls: [...prev.background_audio_urls, ''],
    }));
  };

  const removeBackgroundAudioUrl = (index: number) => {
    setFormData(prev => ({
      ...prev,
      background_audio_urls: prev.background_audio_urls.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handleCorrectOptionToggle = (index: number) => {
    const newCorrectOptions = formData.correct_options.includes(index)
      ? formData.correct_options.filter((i: number) => i !== index)
      : [...formData.correct_options, index];
    setFormData(prev => ({ ...prev, correct_options: newCorrectOptions }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);

      let interactionMetadata: any[] = [];
      if (
        formData.post_type === 'mcqsc' ||
        formData.post_type === 'mcqmc' ||
        formData.post_type === 'poll' ||
        formData.post_type === 'chat_card' ||
        formData.post_type === 'mcq_qna' ||
        formData.post_type === 'joy_questions_card'
      ) {
        const metadata: any = {
          question: formData.question,
          options: formData.options,
        };
        if (
          formData.post_type !== 'poll' &&
          formData.post_type !== 'chat_card' &&
          formData.post_type !== 'mcq_qna' &&
          formData.post_type !== 'joy_questions_card'
        ) {
          metadata.correct_options = formData.correct_options.map(
            (i: number) => i + 1
          );
        }
        interactionMetadata = [metadata];
      }

      if (formData.post_type === 'qna') {
        interactionMetadata = [{ question: formData.question }];
      }

      const payload: any = {
        title: formData.title,
        description: formData.description,
        language: formData.language,
        script: formData.script,
        urls: formData.urls.filter(url => url),
        tags: tagsArray,
        entity_id: storyId,
        author_id: formData.author_id,
        post_type: formData.post_type,
        duration: parseInt(formData.duration) || 0,
        l0_category: formData.l0_category,
        l1_category: formData.l1_category,
        l2_category: formData.l2_category,
        background_audio_urls: formData.background_audio_urls.filter(
          url => url
        ),
        background_color: formData.background_color,
        text_color: formData.text_color,
        type: postType,
        lrc_url: formData.lrc_url,
      };

      if (postType === 'media') {
        if (formData.thumbnail) {
          payload.thumbnail = formData.thumbnail;
        }
        if (formData.content_life_in_days) {
          payload.content_life_in_days = parseInt(
            formData.content_life_in_days
          );
        }
      }

      if (postType === 'system') {
        payload.system_type = formData.system_type;
        if (formData.system_type === 'single_course') {
          const selectedChallenge = masteryChallenges.find(
            c => c.id === formData.course_id
          );
          payload.system_metadata = {
            courses: [
              {
                id: formData.course_id,
                image_url: selectedChallenge?.image_url || '',
              },
            ],
          };
        } else if (formData.system_type === 'multiple_course') {
          payload.system_metadata = {
            courses: formData.courses
              .filter(course => course.course_id)
              .map(course => {
                const selectedChallenge = masteryChallenges.find(
                  c => c.id === course.course_id
                );
                return {
                  id: course.course_id,
                  image_url: selectedChallenge?.image_url || '',
                };
              }),
          };
        }
      }

      if (formData.show_why) {
        const interactiveEntry = { show_why: formData.show_why };
        if (interactionMetadata.length > 0) {
          interactionMetadata[0] = {
            ...interactionMetadata[0],
            ...interactiveEntry,
          };
        } else {
          interactionMetadata = [interactiveEntry];
        }
      }

      if (interactionMetadata.length > 0) {
        payload.interaction_metadata = interactionMetadata;
      }

      const method = editingPost ? 'PUT' : 'POST';
      const url = editingPost
        ? `${config.API_BASE_URL}/feed-post/${editingPost.id}`
        : `${config.API_BASE_URL}/feed-post/`;

      const response = await fetch(url, {
        method,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          editingPost ? 'Failed to update post' : 'Failed to create post'
        );
      }

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        'https://api-test.dev.jumppapp.in/api/v1';

      let postId;
      if (!editingPost) {
        const postData = await response.json();
        postId = postData.id;

        const mapperResponse = await fetch(`${apiBaseUrl}/story-post-mapper/`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
          body: JSON.stringify({
            story_id: storyId,
            post_id: postId,
          }),
        });

        if (!mapperResponse.ok) {
          throw new Error('Failed to map post to story');
        }
      } else {
        postId = editingPost.id;
      }

      if (postType === 'media' && formData.urls[0]) {
        const shouldCallSqs =
          (formData.post_type === 'video' &&
            !formData.urls[0].endsWith('_compressed')) ||
          (formData.post_type === 'image' &&
            !formData.urls[0].endsWith('_compressed')) ||
          formData.post_type === 'audio';

        if (shouldCallSqs) {
          const sqsPayload: any = {
            entity_type: 'feed_post',
            thumbnail: formData.urls[0],
            post_id: postId,
          };

          if (formData.post_type === 'video' && formData.urls[0]) {
            sqsPayload.video_url = formData.urls[0];
          } else if (formData.post_type === 'image' && formData.urls[0]) {
            sqsPayload.image_url = formData.urls[0];
          }

          if (formData.post_type === 'audio') {
            sqsPayload.audio_url = formData.urls[0];
          } else if (formData.background_audio_urls[0]) {
            sqsPayload.audio_url = formData.background_audio_urls[0];
          }

          if (
            sqsPayload.video_url ||
            sqsPayload.image_url ||
            sqsPayload.audio_url
          ) {
            await fetch(`${apiBaseUrl}/upload/sqs-push-data`, {
              method: 'POST',
              headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                ...(token && { 'x-auth-token': token }),
              },
              body: JSON.stringify(sqsPayload),
            });
          }
        }
      }

      onPostCreated?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : editingPost
            ? 'Failed to update post'
            : 'Failed to create post'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {editingPost ? 'Edit Post' : 'Add Post'} - Step {currentStep}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="post_type">Type</label>
            <select
              id="post_type"
              value={postType}
              onChange={e => {
                setPostType(e.target.value);
                if (e.target.value === 'system') {
                  fetchMasteryChallenges();
                }
              }}
              className="form-input"
            >
              <option value="">Select type</option>
              <option value="system">System</option>
              <option value="media">Media</option>
            </select>
          </div>

          {postType === 'system' && (
            <>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={e => handleChange('title', e.target.value)}
                  className="form-input"
                  placeholder="Enter title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="system_type">System Type</label>
                <select
                  id="system_type"
                  value={formData.system_type}
                  onChange={e => handleChange('system_type', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select system type</option>
                  <option value="single_course">Single Course</option>
                  <option value="multiple_course">Multiple Course</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="system_background_color">
                  Background Color
                </label>
                <input
                  type="text"
                  id="system_background_color"
                  value={formData.system_background_color}
                  onChange={e =>
                    handleChange('system_background_color', e.target.value)
                  }
                  className="form-input"
                  placeholder="Enter background color"
                />
              </div>

              {formData.system_type === 'single_course' && (
                <div className="form-group">
                  <label htmlFor="course_id">Course</label>
                  <select
                    id="course_id"
                    value={formData.course_id}
                    onChange={e => handleChange('course_id', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select course</option>
                    {masteryChallenges.map(challenge => (
                      <option key={challenge.id} value={challenge.id}>
                        {challenge.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.system_type === 'multiple_course' && (
                <div className="form-group">
                  <label>Courses</label>
                  {formData.courses.map((course, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        gap: '8px',
                        marginBottom: '8px',
                      }}
                    >
                      <select
                        value={course.course_id}
                        onChange={e =>
                          handleCourseChange(index, 'course_id', e.target.value)
                        }
                        className="form-input"
                        style={{ flex: 1 }}
                      >
                        <option value="">Select course</option>
                        {masteryChallenges.map(challenge => (
                          <option key={challenge.id} value={challenge.id}>
                            {challenge.title}
                          </option>
                        ))}
                      </select>
                      {formData.courses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCourse(index)}
                          className="btn-secondary"
                          style={{ padding: '10px 16px' }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addCourse}
                    className="btn-secondary"
                    style={{ marginTop: '8px' }}
                  >
                    + Add Course
                  </button>
                </div>
              )}
            </>
          )}

          {postType === 'media' && (
            <>
              <div className="form-group">
                <label htmlFor="post_type_field">Post Type</label>
                <select
                  id="post_type_field"
                  value={formData.post_type}
                  onChange={e => handleChange('post_type', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select post type</option>
                  <option value="mcqsc">MCQSC</option>
                  <option value="mcqmc">MCQMC</option>
                  <option value="text">Text</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="image">Image</option>
                  <option value="poll">Poll</option>
                  <option value="qna">QnA</option>
                  <option value="chat_card">Chat Card</option>
                  <option value="mcq_qna">MCQ QnA</option>
                  <option value="joy_questions_card">Joy Questions Card</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={e => handleChange('title', e.target.value)}
                  className="form-input"
                  placeholder="Enter title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={e => handleChange('description', e.target.value)}
                  className="form-input"
                  placeholder="Enter description"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="language">Language</label>
                <select
                  id="language"
                  value={formData.language}
                  onChange={e => handleChange('language', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select language</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="script">Script</label>
                <textarea
                  id="script"
                  value={formData.script}
                  onChange={e => handleChange('script', e.target.value)}
                  className="form-input"
                  placeholder="Enter script"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="urls">URLs</label>
                {formData.urls.map((url, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <input
                      type="text"
                      value={url}
                      onChange={e => handleUrlChange(index, e.target.value)}
                      className="form-input"
                      placeholder="Enter URL"
                      style={{ flex: 1 }}
                    />
                    {formData.urls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeUrl(index)}
                        className="btn-secondary"
                        style={{ padding: '10px 16px' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addUrl}
                  className="btn-secondary"
                  style={{ marginTop: '8px' }}
                >
                  + Add URL
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="tags">Tags</label>
                <input
                  type="text"
                  id="tags"
                  value={formData.tags}
                  onChange={e => handleChange('tags', e.target.value)}
                  className="form-input"
                  placeholder="Enter tags"
                />
              </div>

              <div className="form-group">
                <label htmlFor="duration">Duration (seconds)</label>
                <input
                  type="number"
                  id="duration"
                  value={formData.duration}
                  onChange={e => handleChange('duration', e.target.value)}
                  className="form-input"
                  placeholder="Enter duration"
                />
              </div>

              <div className="form-group">
                <label htmlFor="l0_category">L0 Category</label>
                <input
                  type="text"
                  id="l0_category"
                  value={formData.l0_category}
                  onChange={e => handleChange('l0_category', e.target.value)}
                  className="form-input"
                  placeholder="Enter L0 category"
                />
              </div>

              <div className="form-group">
                <label htmlFor="l1_category">L1 Category</label>
                <input
                  type="text"
                  id="l1_category"
                  value={formData.l1_category}
                  onChange={e => handleChange('l1_category', e.target.value)}
                  className="form-input"
                  placeholder="Enter L1 category"
                />
              </div>

              <div className="form-group">
                <label htmlFor="l2_category">L2 Category</label>
                <input
                  type="text"
                  id="l2_category"
                  value={formData.l2_category}
                  onChange={e => handleChange('l2_category', e.target.value)}
                  className="form-input"
                  placeholder="Enter L2 category"
                />
              </div>

              <div className="form-group">
                <label htmlFor="author_id">Author ID</label>
                <input
                  type="text"
                  id="author_id"
                  value={formData.author_id}
                  onChange={e => handleChange('author_id', e.target.value)}
                  className="form-input"
                  placeholder="Enter author ID"
                />
              </div>

              {formData.post_type === 'audio' && (
                <div className="form-group">
                  <label htmlFor="lrc_url">LRC URL</label>
                  <input
                    type="text"
                    id="lrc_url"
                    value={formData.lrc_url}
                    onChange={e => handleChange('lrc_url', e.target.value)}
                    className="form-input"
                    placeholder="Enter LRC URL"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="thumbnail">Thumbnail</label>
                <input
                  type="text"
                  id="thumbnail"
                  value={formData.thumbnail}
                  onChange={e => handleChange('thumbnail', e.target.value)}
                  className="form-input"
                  placeholder="Enter thumbnail URL"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content_life_in_days">
                  Content Life in Days
                </label>
                <input
                  type="number"
                  id="content_life_in_days"
                  value={formData.content_life_in_days}
                  onChange={e =>
                    handleChange('content_life_in_days', e.target.value)
                  }
                  className="form-input"
                  placeholder="Enter content life in days"
                />
              </div>

              {formData.post_type === 'qna' && (
                <div className="form-group">
                  <label htmlFor="question">Question</label>
                  <textarea
                    id="question"
                    value={formData.question}
                    onChange={e => handleChange('question', e.target.value)}
                    className="form-input"
                    placeholder="Enter question"
                    rows={3}
                  />
                </div>
              )}

              {(formData.post_type === 'mcqsc' ||
                formData.post_type === 'mcqmc' ||
                formData.post_type === 'poll' ||
                formData.post_type === 'chat_card' ||
                formData.post_type === 'mcq_qna' ||
                formData.post_type === 'joy_questions_card') && (
                <>
                  <div className="form-group">
                    <label htmlFor="question">Question</label>
                    <textarea
                      id="question"
                      value={formData.question}
                      onChange={e => handleChange('question', e.target.value)}
                      className="form-input"
                      placeholder="Enter question"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Options</label>
                    {formData.options.map((option: string, index: number) => (
                      <div key={index} style={{ marginBottom: '10px' }}>
                        <input
                          type="text"
                          value={option}
                          onChange={e =>
                            handleOptionChange(index, e.target.value)
                          }
                          className="form-input"
                          placeholder={`Option ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>

                  {formData.post_type !== 'poll' &&
                    formData.post_type !== 'chat_card' &&
                    formData.post_type !== 'mcq_qna' &&
                    formData.post_type !== 'joy_questions_card' && (
                      <div className="form-group">
                        <label>
                          Correct Options (Select{' '}
                          {formData.post_type === 'mcqsc' ? 'one' : 'multiple'})
                        </label>
                        {formData.options.map(
                          (option: string, index: number) => (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '8px',
                              }}
                            >
                              <input
                                type={
                                  formData.post_type === 'mcqsc'
                                    ? 'radio'
                                    : 'checkbox'
                                }
                                checked={formData.correct_options.includes(
                                  index
                                )}
                                onChange={() => {
                                  if (formData.post_type === 'mcqsc') {
                                    setFormData(prev => ({
                                      ...prev,
                                      correct_options: [index],
                                    }));
                                  } else {
                                    handleCorrectOptionToggle(index);
                                  }
                                }}
                                style={{ marginRight: '8px' }}
                              />
                              <label>{option || `Option ${index + 1}`}</label>
                            </div>
                          )
                        )}
                      </div>
                    )}
                </>
              )}

              <div className="form-group">
                <label htmlFor="show_why">Show Why</label>
                <select
                  id="show_why"
                  value={formData.show_why}
                  onChange={e => handleChange('show_why', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="background_color">Background Color</label>
                <input
                  type="text"
                  id="background_color"
                  value={formData.background_color}
                  onChange={e =>
                    handleChange('background_color', e.target.value)
                  }
                  className="form-input"
                  placeholder="Enter background color"
                />
              </div>

              <div className="form-group">
                <label htmlFor="text_color">Text Color</label>
                <input
                  type="text"
                  id="text_color"
                  value={formData.text_color}
                  onChange={e => handleChange('text_color', e.target.value)}
                  className="form-input"
                  placeholder="Enter text color"
                />
              </div>

              <div className="form-group">
                <label htmlFor="background_audio_urls">
                  Background Audio URLs
                </label>
                {formData.background_audio_urls.map((url, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <input
                      type="text"
                      value={url}
                      onChange={e =>
                        handleBackgroundAudioUrlChange(index, e.target.value)
                      }
                      className="form-input"
                      placeholder="Enter background audio URL"
                      style={{ flex: 1 }}
                    />
                    {formData.background_audio_urls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBackgroundAudioUrl(index)}
                        className="btn-secondary"
                        style={{ padding: '10px 16px' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addBackgroundAudioUrl}
                  className="btn-secondary"
                  style={{ marginTop: '8px' }}
                >
                  + Add Background Audio URL
                </button>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {error && (
            <div
              style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#fee',
                color: '#c00',
                borderRadius: '6px',
                width: '100%',
              }}
            >
              {error}
            </div>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !postType}
          >
            {submitting
              ? editingPost
                ? 'Updating...'
                : 'Creating...'
              : editingPost
                ? 'Update Post'
                : 'Create Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPostModal;
